import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { AdminLoadError } from '../components/AdminLoadError';
import { UserIdentity } from '../components/UserIdentity';
import { StatusFilterCheckboxes } from '../components/StatusFilterCheckboxes';
import { useStatusFilterParam } from '../hooks/useStatusFilterParam';
import { useSupportUnreadCounts } from '../hooks/useSupportUnreadCounts';
import type { SupportUserRef } from '@/lib/api';

interface ProblemReport {
  reportId: string;
  // Null once the originating builder session has been deleted — the report
  // itself survives (ON DELETE SET NULL) as a self-contained diagnostic record.
  sessionId: string | null;
  userId: string;
  user?: SupportUserRef | null;
  unread?: boolean;
  description: string;
  problemScope: number;
  sessionStatus: string | null;
  archId: string | null;
  templateId: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  userAgent: string | null;
  pageUrl: string | null;
  status: string;
  adminNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  {
    value: 'investigating',
    label: 'Investigating',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800 border-gray-200' },
];

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <Badge variant="outline" className={config.color}>
      {config.label}
    </Badge>
  );
}

function ScopeBadge({ scope }: { scope: number }) {
  // Problem scope: 1=tiny, 3=small, 5=medium, 10=big. Higher = more painful.
  const color =
    scope >= 10
      ? 'bg-red-100 text-red-800 border-red-200'
      : scope >= 5
        ? 'bg-orange-100 text-orange-800 border-orange-200'
        : scope >= 3
          ? 'bg-amber-100 text-amber-800 border-amber-200'
          : 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return (
    <Badge variant="outline" className={color}>
      Scope {scope}
    </Badge>
  );
}

function summarizeDescription(text: string): string {
  const firstLine = text.split('\n')[0];
  return firstLine.length > 80 ? `${firstLine.slice(0, 77)}…` : firstLine;
}

export function AdminProblemReportsPage() {
  const [reports, setReports] = useState<ProblemReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { statuses, setStatuses } = useStatusFilterParam();
  const { refresh: refreshUnread } = useSupportUnreadCounts();

  // Edit dialog
  const [editReport, setEditReport] = useState<ProblemReport | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteReport, setDeleteReport] = useState<ProblemReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await api.listProblemReportsAdmin({
        status: statuses.length ? statuses : undefined,
        limit: 50,
      });
      setReports(result.reports);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load problem reports:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [statuses]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleEdit = (report: ProblemReport) => {
    setEditReport(report);
    setEditStatus(report.status);
    setEditNotes(report.adminNotes || '');
    if (report.unread) {
      setReports((prev) =>
        prev.map((r) => (r.reportId === report.reportId ? { ...r, unread: false } : r)),
      );
      api
        .markProblemReportReadAdmin(report.reportId)
        .then(() => refreshUnread())
        .catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!editReport) return;
    setSaving(true);
    try {
      await api.updateProblemReportAdmin(editReport.reportId, {
        status: editStatus,
        adminNotes: editNotes.trim() || null,
      });
      setEditReport(null);
      await fetchReports();
    } catch (err) {
      console.error('Failed to update problem report:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteReport) return;
    setDeleting(true);
    try {
      await api.deleteProblemReportAdmin(deleteReport.reportId);
      setDeleteReport(null);
      await fetchReports();
    } catch (err) {
      console.error('Failed to delete problem report:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Arche Builder Reports</h2>
          <p className="text-sm text-muted-foreground">
            {total} report{total !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Status filter (multi-select) */}
      <StatusFilterCheckboxes options={STATUS_OPTIONS} selected={statuses} onChange={setStatuses} />

      {/* Table */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <AdminLoadError what="problem reports" onRetry={fetchReports} />
      ) : reports.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">No problem reports found.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Summary</TableHead>
                <TableHead className="w-[160px]">Customer</TableHead>
                <TableHead className="w-[110px]">Scope</TableHead>
                <TableHead className="w-[130px]">Status</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r) => (
                <TableRow key={r.reportId} className="cursor-pointer" onClick={() => handleEdit(r)}>
                  <TableCell>
                    <p className="flex items-center gap-1.5 text-sm truncate max-w-[400px]">
                      {r.unread && (
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full bg-primary"
                          title="Unread"
                        />
                      )}
                      <span className={r.unread ? 'font-semibold' : 'font-medium'}>
                        {summarizeDescription(r.description)}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground truncate max-w-[400px]">
                      {r.archId ? `Arche ${r.archId}` : 'No arche'} · session{' '}
                      {r.sessionId ?? 'deleted'}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-[160px]">
                    <UserIdentity user={r.user} userId={r.userId} />
                  </TableCell>
                  <TableCell>
                    <ScopeBadge scope={r.problemScope} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteReport(r);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editReport} onOpenChange={(open) => !open && setEditReport(null)}>
        <DialogContent className="max-w-2xl">
          {editReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Problem Report
                  <ScopeBadge scope={editReport.problemScope} />
                  <StatusBadge status={editReport.status} />
                </DialogTitle>
                <DialogDescription>
                  Reported {new Date(editReport.createdAt).toLocaleString()} by{' '}
                  <span title={editReport.userId}>
                    {editReport.user?.displayName ?? editReport.user?.username ?? editReport.userId}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-md bg-muted p-3 max-h-[260px] overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Description:</p>
                <p className="text-sm whitespace-pre-wrap">{editReport.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Session</p>
                  <p className="font-mono truncate">{editReport.sessionId ?? 'deleted'}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Arche</p>
                  <p className="font-mono truncate">{editReport.archId ?? '—'}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Session status</p>
                  <p>{editReport.sessionStatus ?? '—'}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Template</p>
                  <p className="font-mono truncate">{editReport.templateId ?? '—'}</p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Tokens (in / out)</p>
                  <p>
                    {editReport.totalInputTokens.toLocaleString()} /{' '}
                    {editReport.totalOutputTokens.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="font-medium text-muted-foreground">Session cost</p>
                  <p>${(editReport.totalCostCents / 100).toFixed(4)}</p>
                </div>
                {editReport.pageUrl && (
                  <div className="rounded-md bg-muted/50 p-2 col-span-2">
                    <p className="font-medium text-muted-foreground">Page URL</p>
                    <p className="truncate">{editReport.pageUrl}</p>
                  </div>
                )}
                {editReport.reviewedAt && (
                  <div className="rounded-md bg-muted/50 p-2 col-span-2">
                    <p className="font-medium text-muted-foreground">First reviewed</p>
                    <p>
                      {new Date(editReport.reviewedAt).toLocaleString()}{' '}
                      {editReport.reviewedBy ? `by ${editReport.reviewedBy}` : ''}
                    </p>
                  </div>
                )}
              </div>

              {editReport.adminNotes && (
                <div className="rounded-md bg-muted/50 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    Previous admin notes:
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{editReport.adminNotes}</p>
                </div>
              )}

              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select value={editStatus} onValueChange={setEditStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Admin Notes</label>
                  <Textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Internal notes about this report..."
                    rows={3}
                    maxLength={5000}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditReport(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteReport} onOpenChange={(open) => !open && setDeleteReport(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Problem Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this report? This cannot be undone and the captured
              session payload will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteReport(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
